import { CancelOutlined, Search } from "@mui/icons-material";
import * as MUI from "@mui/material";
import React, { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import DateRange from "../../../../components/filter/DateRange";
import SearchBar from "../../../../components/filter/SearchBar";
import FullScreenMessageCore from "../../../../components/fullScreenMessage/FullScreenMessageCore";
import RowStack from "../../../../components/wrappers/RowStack";
import { AppointmentFilteringContext } from "../../../../contexts/AppointmentFiltering";
import { STATUSES } from "../../../../enums/AppointmentStatus";
import { CHIP_MIN_WIDTH } from "../../../../enums/Styles";
import useUser from "../../../../hooks/useUser";
import * as F from "../../utils/Functions";
import * as ENUM from "../enums/TableHeaderData";
import AppointmentTableRow from "./AppointmentTableRow";

export const appointmentParamKeys = {
  status: "status",
  endDate: "to",
  startDate: "from",
  search: "q",
  tableRow: "row",
  tablePage: "page",
};

//Keep in mind, these values need to be changed if we change the names of the fields being sorted
export const orderStrings = {
  status: "statusord",
  date: "startdateord",
  service: "typeord",
  email: "emailord",
  phone: "phonenumberord",
  pet: "pet_nameord",
};

export const handlers = {
  orderBy: "orderby",
};

export function getStatusOrder(element) {
  switch (element.status) {
    case STATUSES.WAITING_CLINIC:
      return 1;
    case STATUSES.ACTIVE:
      return 2;
    case STATUSES.FINISHED:
      return 4;
    case STATUSES.INACTIVE:
      return 5;
    case STATUSES.RESOLVED:
      return 6;
    case STATUSES.WAITING_USER:
      return 7;
    default:
      return 8;
  }
}

export default function DesktopReservations() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    data,
    isLoading,
    handleWildCardData,
    handleStatusFiltering,
    resetFilter,
  } = React.useContext(AppointmentFilteringContext);

  const [order, setOrder] = React.useState("desc");
  const [orderBy, setOrderBy] = React.useState("startDate");
  const { preferance } = useUser();

  const handleRequestSort = (event, property) => {
    if (searchParams.get(handlers.orderBy) != null) {
      searchParams.delete(handlers.orderBy); // Ensure old param is deleted
    }

    const isAsc = orderBy === property && order === "asc";
    const newOrder = isAsc ? "desc" : "asc";

    setOrder(newOrder);
    setOrderBy(property);

    searchParams.set(
      handlers.orderBy,
      `${String(property + "ord").toLowerCase()}=${newOrder}`,
    );

    setSearchParams(searchParams);
  };

  const handleChangeRowsPerPage = (e) => {
    searchParams.set(appointmentParamKeys.tableRow, Number(e.target.value));
    searchParams.set(appointmentParamKeys.tablePage, 0);
    setSearchParams(searchParams);
  };

  const handleChangePage = (e, newPage) => {
    searchParams.delete(appointmentParamKeys.tablePage);
    searchParams.set(appointmentParamKeys.tablePage, Number(newPage));
    setSearchParams(searchParams);
  };

  const createSortHandler = (property) => (event) => {
    handleRequestSort(event, property);
    searchParams.set(appointmentParamKeys.tablePage, 0);
    setSearchParams(searchParams);
  };

  function isChipActive(status) {
    const statusParams = searchParams.get(appointmentParamKeys.status);
    const prev = statusParams ? statusParams.split("+") : [];
    return prev.includes(String(status)) ? "primary" : "default";
  }

  function getRows() {
    if (searchParams.get(appointmentParamKeys.tableRow) == null) {
      return preferance.appointmentTableRowsPerPage ?? 10;
    }
    return searchParams.get(appointmentParamKeys.tableRow) ?? 10;
  }

  function getPage() {
    const page = Number(searchParams.get(appointmentParamKeys.tablePage));
    if (page < 0) {
      searchParams.set(appointmentParamKeys.tablePage, 0);
      setSearchParams(searchParams);
      return 0;
    } else {
      return page;
    }
  }

  const handlePageInputChange = (event) => {
    handleChangePage(null, Number(event.target.value) - 1);
  };

  const loading = data == null || data == undefined || isLoading;

  return (
    <MUI.Box marginTop={11}>
      <>
        <MUI.Grid container spacing={2} alignItems="center">
          <MUI.Grid item xs={11}>
            <MUI.Box>
              {F.statusTitle.map((status) => (
                <MUI.Chip
                  icon={F.getIconByStatus(status.value)}
                  sx={{
                    m: 1,
                    p: 1,
                    minWidth: CHIP_MIN_WIDTH - 20,
                    justifyContent: "start",
                  }}
                  label={t(status.title)}
                  onClick={() => handleStatusFiltering(status.value)}
                  color={isChipActive(status.value)}
                  key={status.title}
                  data-testid={`appointment.admin-table.status-chip-${status.value}`}
                />
              ))}
            </MUI.Box>
          </MUI.Grid>

          <MUI.Grid item xs={1}>
            <MUI.Button
              startIcon={<CancelOutlined />}
              color="error"
              variant="contained"
              onClick={() => resetFilter()}
              sx={{
                boxShadow: "none",
              }}
              data-testid={"appointment.admin-table.reset-button"}
            >
              {t("Reset")}
            </MUI.Button>
          </MUI.Grid>
        </MUI.Grid>
        <RowStack>
          <SearchBar
            lsVal={appointmentParamKeys.search}
            getData={(value) => handleWildCardData(value)}
            testId={"appointment.admin-table.search-input"}
          />
          <DateRange
            pageParam={appointmentParamKeys.tablePage}
            paramsEnd={appointmentParamKeys.endDate}
            paramsStart={appointmentParamKeys.startDate}
            testId={"appointment.admin-table.date-range"}
          />
        </RowStack>
        
        {loading ? (
          <MUI.TableContainer sx={{ minHeight: 620, maxHeight: 620 }}>
            <MUI.Table sx={{ minWidth: 750 }}>
              <MUI.Stack
                display={"flex"}
                direction={"column"}
                justifyContent={"center"}
                alignItems={"center"}
                height={"100%"}
              >
                <MUI.CircularProgress />
              </MUI.Stack>
            </MUI.Table>
          </MUI.TableContainer>
        ) : data["appointments"]?.length > 0 ? (
          <Fragment>
            <MUI.TableContainer sx={{ minHeight: 620, maxHeight: 620 }}>
              <MUI.Table sx={{ minWidth: 750 }}>
                <MUI.TableHead>
                  <MUI.TableRow>
                    {ENUM.TableHeaderColumn.map((item) => (
                      <MUI.TableCell
                        sortDirection={orderBy === item.id ? order : false}
                        align={ENUM.TABLE_CELL_ALIGNMENT}
                        key={item.id}
                        sx={{ pl: 5, minWidth: 150 }}
                      >
                        <MUI.TableSortLabel
                          active={orderBy === item.id}
                          direction={orderBy === item.id ? order : "asc"}
                          onClick={createSortHandler(item.id)}
                          data-testid={`appointment.admin-table.sort-${item.id}`}
                        >
                          {t(item.label)}
                          {orderBy === item.id ? (
                            <MUI.Box component="span" sx={{ display: "none" }}>
                              {order === "desc"
                                ? "sorted descending"
                                : "sorted ascending"}
                            </MUI.Box>
                          ) : null}
                        </MUI.TableSortLabel>
                      </MUI.TableCell>
                    ))}
                  </MUI.TableRow>
                </MUI.TableHead>
                <MUI.TableBody>
                  {data["appointments"].map((data, index) => (
                    <AppointmentTableRow
                      data={data}
                      index={index}
                      key={data.id}
                    />
                  ))}
                </MUI.TableBody>
              </MUI.Table>
            </MUI.TableContainer>

            <MUI.Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "end",
              }}
            >
              <MUI.Select
                value={getPage() + 1}
                onChange={handlePageInputChange}
                size="small"
                SelectDisplayProps={{ "data-testid": "appointment.admin-table.page-select" }}
              >
                {Array.from(
                  { length: Math.ceil(Number(data["pages"]) / getRows()) },
                  (_, i) => i + 1,
                ).map((num) => (
                  <MUI.MenuItem value={num}>{num}</MUI.MenuItem>
                ))}
              </MUI.Select>
              <MUI.TablePagination
                rowsPerPageOptions={[10, 25, 50, 100]}
                component="div"
                count={data["pages"]}
                labelRowsPerPage={t("RowsPerPage")}
                rowsPerPage={getRows()}
                page={getPage()}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                backIconButtonProps={{
                  title: t("PreviousPage"),
                  "aria-label": t("PreviousPage"),
                  "data-testid": "appointment.admin-table.prev-page-button",
                }}
                nextIconButtonProps={{
                  title: t("NextPage"),
                  "aria-label": t("NextPage"),
                  "data-testid": "appointment.admin-table.next-page-button",
                }}
                SelectProps={{
                  SelectDisplayProps: {
                    "data-testid": "appointment.admin-table.rows-per-page-select",
                  },
                }}
              />
            </MUI.Box>
          </Fragment>
        ) : (
          <NoData/>
        )}
      </>
    </MUI.Box>
  );
}

function NoData() {
  return (
    <MUI.Stack sx={{ minHeight: 580 }} data-testid={"appointment.admin-table.no-data"}>
      <FullScreenMessageCore
        withoutWrapper={false}
        shouldCenter={false}
        centerRelative={true}
        containerBoxShadow={undefined}
        title={"NoSearchResults"}
        icon={<Search className="Mui-fullscreen-icon" />}
        containerPadding={3}
      />
    </MUI.Stack>
  );
}
